/**
 * At-rest sealing for the outbox and drafts (R10, N-F4-04): the same per-device key as the
 * roster (vault.ts, issued with the roster). Only the command payload and the draft's data are
 * sealed — status, type, session_id, kind and the timestamps stay plain because they are indexed
 * and the sync engine reads them. Without a key (no roster yet, or the server issued none) values
 * are stored plain, as before.
 *
 * WebCrypto promises end an IndexedDB transaction, so values are sealed before a write and opened
 * after a read, never inside a Dexie transaction.
 *
 * Each sealed value carries `kid`, the id of the key that sealed it, so a reader picks the right
 * key and `resealLocalData` only touches rows sealed with an older key (or not sealed at all).
 */
import type { DraftRecord, OutboxRecord, SupplyChainDB } from './db'
import { getVaultKeys, type VaultKeys } from './roster'
import { isSealed, seal, unseal, type SealedBox } from './vault'

export type KeyedBox = SealedBox & { kid?: string | null }

/** A sealed value no key on this phone opens (the device was wiped, or the key was lost). */
export class LockedValueError extends Error {
  constructor() {
    super('Stored on this phone encrypted, and the key to open it is gone')
    this.name = 'LockedValueError'
  }
}

/** Seal with the current device key, or keep plain when there is none. */
export async function sealValue<T>(value: T, db: SupplyChainDB): Promise<T | KeyedBox> {
  const keys = await getVaultKeys(db)
  if (!keys.key) return value
  return { ...(await seal(keys.key, value)), kid: keys.id }
}

function keysToTry(keys: VaultKeys, box: KeyedBox): CryptoKey[] {
  const out: CryptoKey[] = []
  if (box.kid !== undefined && box.kid !== null && box.kid === keys.prevId && keys.prev)
    out.push(keys.prev)
  if (keys.key) out.push(keys.key)
  if (keys.prev && !out.includes(keys.prev)) out.push(keys.prev)
  return out
}

/** Open a value that may or may not be sealed. Throws LockedValueError if no key opens it. */
export async function openValue<T>(data: T | KeyedBox, db: SupplyChainDB): Promise<T> {
  if (!isSealed(data)) return data
  const keys = await getVaultKeys(db)
  for (const key of keysToTry(keys, data as KeyedBox)) {
    try {
      return await unseal<T>(key, data)
    } catch {
      // wrong key: try the next one
    }
  }
  throw new LockedValueError()
}

// ---------- outbox ----------

/** The record as stored: payload sealed when the phone has a key. */
export async function sealOutboxRecord(
  rec: OutboxRecord,
  db: SupplyChainDB,
): Promise<OutboxRecord> {
  return { ...rec, payload: await sealValue(rec.payload, db) }
}

export interface OpenedOutboxRecord extends OutboxRecord {
  /** True when no key on this phone opens the payload (payload is then null). */
  payload_locked?: true
}

/** Open every record's payload; a payload no key opens comes back null with `payload_locked`. */
export async function openOutboxRecords(
  records: readonly OutboxRecord[],
  db: SupplyChainDB,
): Promise<OpenedOutboxRecord[]> {
  return Promise.all(
    records.map(async (r) => {
      try {
        return { ...r, payload: await openValue(r.payload, db) }
      } catch (e) {
        if (!(e instanceof LockedValueError)) throw e
        return { ...r, payload: null, payload_locked: true as const }
      }
    }),
  )
}

/** For screens and projections: the readable records only, payloads opened. */
export async function readableOutbox(
  records: readonly OutboxRecord[],
  db: SupplyChainDB,
): Promise<OutboxRecord[]> {
  return (await openOutboxRecords(records, db)).filter((r) => !r.payload_locked)
}

// ---------- drafts ----------

/** Open a draft; undefined when no key opens it (a wiped phone's draft is gone, not an error). */
export async function openDraft<T>(
  row: DraftRecord | undefined,
  db: SupplyChainDB,
): Promise<DraftRecord<T> | undefined> {
  if (!row) return undefined
  try {
    return { ...row, data: await openValue<T>(row.data as T, db) }
  } catch (e) {
    if (e instanceof LockedValueError) return undefined
    throw e
  }
}

export async function openDrafts<T>(
  rows: readonly DraftRecord[],
  db: SupplyChainDB,
): Promise<DraftRecord<T>[]> {
  const opened = await Promise.all(rows.map((r) => openDraft<T>(r, db)))
  return opened.filter((r): r is DraftRecord<T> => r !== undefined)
}

// ---------- reseal ----------

function needsReseal(data: unknown, currentId: string | null): boolean {
  if (!isSealed(data)) return true
  return ((data as KeyedBox).kid ?? null) !== currentId
}

function sameStored(a: unknown, b: unknown): boolean {
  if (isSealed(a) && isSealed(b)) {
    const x = new Uint8Array(a.iv)
    const y = new Uint8Array(b.iv)
    return x.length === y.length && x.every((v, i) => v === y[i])
  }
  if (isSealed(a) || isSealed(b)) return false
  return JSON.stringify(a) === JSON.stringify(b)
}

/**
 * Seal with the current key every outbox payload and draft that is still plain (made before the
 * phone had a key) or sealed with an older key. Run after the key arrives or changes. A row
 * rewritten meanwhile (a draft saved, a payload scrubbed) is left for the next run rather than
 * overwritten with older data. Rows no key opens are left as they are.
 */
export async function resealLocalData(
  db: SupplyChainDB,
): Promise<{ outbox: number; drafts: number }> {
  const keys = await getVaultKeys(db)
  if (!keys.key) return { outbox: 0, drafts: 0 }

  const outbox: Array<{ seq: number; was: unknown; now: unknown }> = []
  for (const r of await db.outbox.toArray()) {
    if (r.seq === undefined || !needsReseal(r.payload, keys.id)) continue
    try {
      const plain = await openValue(r.payload, db)
      outbox.push({ seq: r.seq, was: r.payload, now: await sealValue(plain, db) })
    } catch (e) {
      if (!(e instanceof LockedValueError)) throw e
    }
  }
  const drafts: Array<{ id: string; updatedAt: number; was: unknown; now: unknown }> = []
  for (const d of await db.drafts.toArray()) {
    if (!needsReseal(d.data, keys.id)) continue
    try {
      const plain = await openValue(d.data, db)
      drafts.push({
        id: d.id,
        updatedAt: d.updated_at,
        was: d.data,
        now: await sealValue(plain, db),
      })
    } catch (e) {
      if (!(e instanceof LockedValueError)) throw e
    }
  }

  let o = 0
  let n = 0
  await db.transaction('rw', db.outbox, db.drafts, async () => {
    for (const x of outbox) {
      const cur = await db.outbox.get(x.seq)
      if (cur && sameStored(cur.payload, x.was)) {
        await db.outbox.update(x.seq, { payload: x.now })
        o++
      }
    }
    for (const x of drafts) {
      const cur = await db.drafts.get(x.id)
      if (cur && cur.updated_at === x.updatedAt && sameStored(cur.data, x.was)) {
        await db.drafts.update(x.id, { data: x.now })
        n++
      }
    }
  })
  return { outbox: o, drafts: n }
}
